import { HttpService } from "../../modules/shared/services/http.service";
import { 
  QuotePricingModel, 
  QuotePricingCreateDto, 
  QuotePricingUpdateDto,
  QuotePricingResponse,
  QuotePricingRegion,
  QuotePricingRegionCreateDto,
  QuotePricingRegionUpdateDto,
  QuotePricingRegionResponse,
  QuotePricingRegionBulkCreateDto,
  QuotePricingRegionBulkUpdateDto
} from "../../modules/shared/model/admin-quote-pricing.interfaces";

const headers = { "Content-Type": "application/json", "x-custom-lang": "en" } as const;

// ===== QUOTE PRICINGS API ACTIONS =====

// Get all quote pricings with pagination
export const getQuotePricings = async (query?: Record<string, string | number | boolean>): Promise<QuotePricingResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/quote-pricings${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  console.log("API Response:", response);
  
  // Transform the response to match the expected interface
  const transformedResponse: QuotePricingResponse = {
    data: response.data || [],
    hasNextPage: response.hasNextPage || false,
    hasPreviousPage: response.hasPreviousPage || false,
    page: response.page || 1,
    limit: response.limit || 10,
    totalCount: response.totalCount || 0
  };
  
  console.log("Transformed Response:", transformedResponse);
  return transformedResponse;
};

// Get quote pricing by ID
export const getQuotePricingById = async (id: string): Promise<QuotePricingModel> => {
  const response = await HttpService.get(`api/v1/quote-pricings/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

// Create new quote pricing
export const createQuotePricingAction = async (data: QuotePricingCreateDto): Promise<QuotePricingModel> => {
  const response = await HttpService.post("api/v1/quote-pricings", data, headers, true);
  console.log(response,"response");
  return response;
};

// Update quote pricing
export const updateQuotePricingAction = async (id: string, data: QuotePricingUpdateDto): Promise<QuotePricingModel> => {
  const response = await HttpService.patch(`api/v1/quote-pricings/${id}`, data, headers, true);
  return response;
};

// Delete quote pricing
export const deleteQuotePricingAction = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/quote-pricings/${id}`, { "x-custom-lang": "en" }, true);
};

// ===== QUOTE PRICING REGIONS API ACTIONS =====

// Get all quote pricing regions with pagination
export const getQuotePricingRegions = async (query?: Record<string, string | number | boolean>): Promise<QuotePricingRegionResponse> => {
  const qs = new URLSearchParams();
  Object.entries(query || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qs.append(k, String(v));
  });
  const response = await HttpService.get(`api/v1/quote-pricing-regions${qs.toString() ? `?${qs}` : ""}`, true, { "x-custom-lang": "en" });
  return response;
};

// Get quote pricing region by ID
export const getQuotePricingRegionById = async (id: string): Promise<QuotePricingRegion> => {
  const response = await HttpService.get(`api/v1/quote-pricing-regions/${id}`, true, { "x-custom-lang": "en" });
  return response;
};

// Create new quote pricing region
export const createQuotePricingRegionAction = async (data: QuotePricingRegionCreateDto): Promise<QuotePricingRegion> => {
  const response = await HttpService.post("api/v1/quote-pricing-regions", data, headers, true);
  return response;
};

// Update quote pricing region
export const updateQuotePricingRegionAction = async (id: string, data: QuotePricingRegionUpdateDto): Promise<QuotePricingRegion> => {
  const response = await HttpService.patch(`api/v1/quote-pricing-regions/${id}`, data, headers, true);
  return response;
};

// Delete quote pricing region
export const deleteQuotePricingRegionAction = async (id: string): Promise<void> => {
  await HttpService.deleteRecord(`api/v1/quote-pricing-regions/${id}`, { "x-custom-lang": "en" }, true);
};

// ===== BULK OPERATIONS FOR QUOTE PRICING REGIONS =====

// Bulk create quote pricing regions
export const bulkCreateQuotePricingRegionsAction = async (data: QuotePricingRegionBulkCreateDto): Promise<{
  success: QuotePricingRegion[];
  errors: Array<{ item: any; error: string }>;
  total: number;
  successCount: number;
  errorCount: number;
}> => {
  const response = await HttpService.post("api/v1/quote-pricing-regions/bulk", data, headers, true);
  return response;
};

// Bulk update quote pricing regions
export const bulkUpdateQuotePricingRegionsAction = async (data: QuotePricingRegionBulkUpdateDto): Promise<{
  success: QuotePricingRegion[];
  errors: Array<{ id: string; item: any; error: string }>;
  total: number;
  successCount: number;
  errorCount: number;
}> => {
  const response = await HttpService.patch("api/v1/quote-pricing-regions/bulk", data, headers, true);
  return response;
};

// ===== QUOTE PRICING LOOKUP API ACTIONS =====

/**
 * Get quote pricings by category ID
 * Used for calculating prices when creating/updating jobs
 */
export const getQuotePricingsByCategory = async (categoryId: string): Promise<QuotePricingModel[]> => {
  const response = await HttpService.get(`api/v1/quote-pricings?category=${categoryId}`, true, { "x-custom-lang": "en" });
  return response.data || [];
};

/**
 * Get quote pricings by category and region
 * More specific lookup for accurate pricing
 */
export const getQuotePricingsByCategoryAndRegion = async (params: {
  categoryId: string;
  regionId?: string;
  province?: string;
  municipality?: string;
}): Promise<QuotePricingModel[]> => {
  const qs = new URLSearchParams();
  qs.append("category", params.categoryId);
  
  if (params.regionId) qs.append("region", params.regionId);
  if (params.province) qs.append("province", params.province);
  if (params.municipality) qs.append("municipality", params.municipality);
  
  const response = await HttpService.get(`api/v1/quote-pricings?${qs.toString()}`, true, { "x-custom-lang": "en" });
  return response.data || [];
};
